import * as React from 'react';
import Box from '@mui/material/Box';
import {ReactNode} from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import DialogButton from "../form/DialogButton";
import IconicButton from "../controls/IconicButton";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import {useResources} from "../../stores/ResourceProvider";
import FormValidator from "../form/FormValidator";
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import {Tooltip} from "@mui/material";
import useIsMobile from "../useIsMobile";

export type ModelEditDialogSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const ModelEditDialog = ({
  title,
  open,
  size,
  fullWidth,
  sxContent,
  children,
  onCancel,
  onSave,
  onNext,
  onPrev,
  onDelete,
  confirmDelete,
  archived,
  onArchive,
  confirmArchive
}: {
  title: string,
  open: boolean,
  size?: ModelEditDialogSize
  fullWidth?: boolean
  sxContent?: any
  children: ReactNode
  onCancel?: any
  onSave?: any
  onNext?: any
  onPrev?: any
  onDelete?: any
  confirmDelete?: boolean
  archived?: boolean
  onArchive?(archive: boolean): any
  confirmArchive?: boolean
}) => {

  const { confirm } = useResources()
  const isMobile = useIsMobile()

  const styles = {
    dialogContent: {
      mt:0,
      mb:2,
      px:0,
      pb:0,
      maxHeight:isMobile ? "calc(100vh - 306px)" : "calc(100vh - 200px)",
      overflowY:"scroll",
      '&.MuiDialogContent-root': {
        paddingLeft: "16px",
        paddingRight: "16px"
      }
    },
  }

  const handleSave = (event: any) => {
    if (event.nativeEvent.submitter.id === 'next' && onNext) {
      onNext(event)
    } else if (event.nativeEvent.submitter.id === 'prev' && onPrev) {
      onPrev(event)
    } else if (event.nativeEvent.submitter.id === 'save' && onSave) {
      onSave(event)
    }
  }

  const handleClose = (event: any, reason: string) => {
    if (reason !== 'backdropClick') {
      onCancel()
    }
  }

  const handleDelete = (event: any) => {
    if (confirmDelete === undefined || confirmDelete === true) {
      confirm.show('Confirm Delete', `Are you sure you want to delete this item? It will be permanently deleted from all snapshots. Alternatively, you can archive it.`,
        ['Delete', 'Cancel'],
        () => {
          onDelete()
          return true
        })
    } else {
      onDelete()
    }
  }

  const handleArchive = (event: any) => {
    if (onArchive)
      if (!archived && (confirmArchive === undefined || confirmArchive === true)) {
        confirm.show('Confirm Archive', `Are you sure you want to archive this item?`,
          ['Archive', 'Cancel'],
          () => {
            onArchive(true)
            return true
          })
      } else {
        onArchive(false)
    }
  }

  return (
    <Dialog
      id="ModelEditDialog"
      open={open}
      onClose={handleClose}
      maxWidth={size ?? 'xs'}
      fullWidth={fullWidth !== undefined ? fullWidth : true}
      aria-modal={true}
      aria-labelledby="ModelEditDialogTitle"
      sx={isMobile ? {
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          '& .MuiPaper-root': {
            margin: {
              xs: '8px', // smaller margin on mobile
              sm: '32px', // default margin on larger screens
            },
            width: '100%',
            maxWidth: {
              xs: 'calc(100% - 16px)', // accounts for the smaller margin
              sm: '600px',
            },
          },
        },
      } : undefined}
    >
      <FormValidator
        id="modelEditForm"
        name="modelEditForm"
        onSubmit={handleSave}
      >
        <DialogTitle id="ModelEditDialogTitle">
          {title} {archived ? "(Archived)" : ""}
        </DialogTitle>
        <DialogContent sx={sxContent ?? styles.dialogContent}>
          {children}
        </DialogContent>
        <DialogActions>
          <Box display="flex" flexGrow={1} justifyContent={onDelete || onArchive ? "space-between" : "flex-end"} alignItems="center">
            <Box display="flex" justifyContent="flex-start">
              {onDelete &&
                <IconicButton onClick={handleDelete} testId="dialog-delete">
                  <Tooltip title="Delete item" placement="top">
                    <DeleteIcon/>
                  </Tooltip>
                </IconicButton>
              }
              {onArchive &&
                <Box ml={1}>
                  <IconicButton onClick={handleArchive} testId="dialog-archive">
                    <Tooltip title={archived ? "Unarchive item": "Archive item"} placement="top">
                    {archived ? <UnarchiveIcon/> : <ArchiveOutlinedIcon/>}
                    </Tooltip>
                  </IconicButton>
                </Box>
              }
            </Box>
            <Box display="flex" justifyContent="flex-end">
              <DialogButton id="cancel" variant="secondary" type="button" onClick={onCancel}>
                Cancel
              </DialogButton>
              {onPrev &&
                <DialogButton id="prev" variant="secondary" type="submit" spacing="left">
                  Prev
                </DialogButton>
              }
              {onNext &&
                <DialogButton id="next" variant="primary" type="submit" spacing="left">
                  Next
                </DialogButton>
              }
              {!onNext &&
                <DialogButton id="save" variant="primary" type="submit" spacing="left" disabled={!onSave || archived}>
                  Save
                </DialogButton>
              }
            </Box>
          </Box>
        </DialogActions>
      </FormValidator>
    </Dialog>
  );

}

export default ModelEditDialog